import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  Sparkles,
  FileText,
  Settings,
  CreditCard,
  Menu,
  X,
  LogOut,
  BarChart3,
  Inbox,
  Users,
  Code,
  LayoutDashboard,
  Plug,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import customLogo from '@/assets/favicon-removebg-preview.png';

const getNavigation = (userPlan: string) => {
  const nav = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Forms', href: '/forms', icon: FileText },
    { name: 'Submissions', href: '/submissions', icon: Inbox },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'CRM', href: '/crm', icon: Users },
    { name: 'Integrations', href: '/integrations', icon: Plug },
  ];

  if (['pro', 'business', 'enterprise'].includes(userPlan?.toLowerCase() || '')) {
    nav.push({ name: 'API', href: '/api', icon: Code });
  }

  return nav;
};

const bottomNav = [
  { name: 'Billing', href: '/billing-new', icon: CreditCard },
  { name: 'Settings', href: '/settings', icon: Settings },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('bravax_sidebar_collapsed') === 'true';
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('bravax_sidebar_collapsed', String(next));
      return next;
    });
  };

  // The icon-only look is desktop-only; the mobile drawer always shows labels
  const collapsed = isCollapsed && !mobileOpen;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = getNavigation(user?.plan || 'free');

  return (
    <div className="h-[100dvh] w-full flex bg-background font-sans p-3 sm:p-4 gap-4 overflow-hidden">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden animate-in fade-in duration-300"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Floating Card Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-3 left-3 sm:inset-y-4 sm:left-4 z-50 w-64 bg-card border border-border/60 rounded-[2rem] flex flex-col transition-all duration-300 shadow-2xl overflow-hidden shrink-0',
          'lg:static lg:inset-auto lg:h-full lg:translate-x-0',
          isCollapsed ? 'lg:w-[80px]' : 'lg:w-64',
          mobileOpen ? 'translate-x-0' : '-translate-x-[calc(100%+2rem)]'
        )}
      >
        {/* Header: Logo & Collapse Button */}
        <div className={cn("flex items-center shrink-0", collapsed ? "flex-col justify-center gap-2 py-4 px-2" : "h-20 px-6 justify-between")}>
          <div className="flex items-center gap-3 cursor-pointer overflow-hidden" onClick={() => navigate('/dashboard')}>
            <img src={customLogo} alt="Bravax Forms" className="h-8 w-8 object-contain shrink-0" />
            {!collapsed && (
              <span className="font-sans font-bold text-lg text-foreground tracking-tight whitespace-nowrap">
                Bravax Forms
              </span>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCollapse}
            className="hidden lg:flex h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 shrink-0"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>

          <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8" onClick={() => setMobileOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* AI Action Button */}
        <div className="px-4 mb-2 shrink-0">
          <Button
            onClick={() => navigate('/ai-builder')}
            className={cn(
              "w-full rounded-2xl bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/20 hover:scale-[1.02] transition-all",
              collapsed ? "h-12 w-12 p-0 justify-center mx-auto" : "h-11 justify-start px-4 gap-2"
            )}
            title="Create with AI"
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="text-sm">Create with AI</span>}
          </Button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto overflow-x-hidden">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <button
                key={item.name}
                onClick={() => { navigate(item.href); setMobileOpen(false); }}
                title={collapsed ? item.name : undefined}
                className={cn(
                  'w-full flex items-center rounded-2xl text-sm font-medium transition-all group relative',
                  collapsed ? 'justify-center h-12 w-12 mx-auto' : 'px-4 py-3 gap-3.5',
                  isActive
                    ? 'bg-primary/10 text-primary font-semibold shadow-sm border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                )}
              >
                <item.icon className={cn("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isActive ? "text-primary" : "text-muted-foreground")} />
                {!collapsed && <span className="truncate">{item.name}</span>}
              </button>
            );
          })}

          <div className="pt-4 border-t border-border/30 my-2 space-y-1.5">
            {bottomNav.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <button
                  key={item.name}
                  onClick={() => { navigate(item.href); setMobileOpen(false); }}
                  title={collapsed ? item.name : undefined}
                  className={cn(
                    'w-full flex items-center rounded-2xl text-sm font-medium transition-all group relative',
                    collapsed ? 'justify-center h-12 w-12 mx-auto' : 'px-4 py-3 gap-3.5',
                    isActive
                      ? 'bg-primary/10 text-primary font-semibold shadow-sm border border-primary/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  )}
                >
                  <item.icon className={cn("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isActive ? "text-primary" : "text-muted-foreground")} />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Profile / Logout Footer Row (Contact support removed) */}
        <div className="p-3 mt-auto border-t border-border/40 bg-muted/20 shrink-0">
          {!collapsed ? (
            <div className="flex items-center justify-between px-1 py-1">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar className="h-8 w-8 border border-border shrink-0">
                  <AvatarFallback className="bg-primary/20 text-primary font-bold text-xs">
                    {(user?.name?.charAt(0) || "U").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-foreground truncate leading-tight">{user?.name || "User"}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-tight">{user?.plan || 'Free'} Plan</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <ThemeToggle />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={handleLogout}>
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-1">
              <Avatar className="h-8 w-8 border border-border">
                <AvatarFallback className="bg-primary/20 text-primary font-bold text-xs">
                  {(user?.name?.charAt(0) || "U").toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={handleLogout} title="Log out">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area — Fits screen height, no outer page scrolling */}
      <div className="flex-1 flex flex-col min-w-0 h-full relative overflow-hidden bg-card/30 border border-border/40 rounded-[2rem]">
        {/* Top Header for Mobile */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border/40 lg:hidden shrink-0">
          <div className="flex h-16 items-center justify-between px-6">
            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src={customLogo} alt="Bravax Forms" className="h-6 w-6" />
              <span className="font-bold text-sm text-foreground">Bravax Forms</span>
            </div>
            <div className="w-10" />
          </div>
        </header>

        {/* Main Scrollable Viewport */}
        <main className="flex-1 overflow-y-auto scroll-smooth">
          <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
